import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AppContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const translations = {
  ro: {
    home: 'Acasă',
    shop: 'Magazin',
    collections: 'Colecții',
    blog: 'Blog',
    contact: 'Contact',
    about: 'Despre noi',
    cart: 'Coș',
    search: 'Caută',
    login: 'Autentificare',
    register: 'Înregistrare',
    logout: 'Deconectare',
    account: 'Contul meu',
    addToCart: 'Adaugă în coș',
    buyNow: 'Cumpără acum',
    viewDetails: 'Vezi detalii',
    price: 'Preț',
    quantity: 'Cantitate',
    total: 'Total',
    checkout: 'Finalizare comandă',
    continueShopping: 'Continuă cumpărăturile',
    emptyCart: 'Coșul este gol',
    featured: 'Produse recomandate',
    newArrivals: 'Noutăți',
    onSale: 'Reduceri',
    categories: 'Categorii',
    filters: 'Filtre',
    sortBy: 'Sortează după',
    currency: 'Monedă',
    language: 'Limbă',
    shipping: 'Livrare',
    payment: 'Plată',
    orderSummary: 'Sumar comandă',
    placeOrder: 'Plasează comanda',
    payOnDelivery: 'Plată la livrare',
    payWithCard: 'Plată cu cardul',
    subtotal: 'Subtotal',
    tax: 'TVA',
    shippingAddress: 'Adresa de livrare',
    billingAddress: 'Adresa de facturare',
    firstName: 'Prenume',
    lastName: 'Nume',
    email: 'Email',
    phone: 'Telefon',
    address: 'Adresă',
    city: 'Oraș',
    country: 'Țară',
    postalCode: 'Cod poștal',
    guestCheckout: 'Comandă fără cont',
    readMore: 'Citește mai mult',
    allRightsReserved: 'Toate drepturile rezervate',
    privacyPolicy: 'Politica de confidențialitate',
    termsConditions: 'Termeni și condiții',
    heroTitle: 'Lux și Confort pentru Casa Ta',
    heroSubtitle: 'Descoperă colecția noastră de lenjerii premium',
    shopNow: 'Cumpără acum',
    discoverCollection: 'Descoperă colecția',
    qualityMaterials: 'Materiale de calitate',
    freeShipping: 'Livrare gratuită',
    freeShippingDesc: 'Pentru comenzi peste 349 RON',
    easyReturns: 'Retururi în 30 zile',
    easyReturnsDesc: 'Politică de returnare',
    securePayment: 'Plată securizată',
    securePaymentDesc: 'Checkout criptat SSL',
    // Footer pages
    information: 'Informații',
    paymentMethods: 'Metode de plată',
    shippingDelivery: 'Livrare și expediere',
    returnPolicy: 'Politica de retur',
    returnForm: 'Formular retur',
    ranistore: 'RaniStore',
    aboutUs: 'Despre noi',
    cookiesPolicy: 'Politica cookies',
    personalData: 'Prelucrarea datelor personale',
    anpc: 'ANPC',
  },
  en: {
    home: 'Home',
    shop: 'Shop',
    collections: 'Collections',
    blog: 'Blog',
    contact: 'Contact',
    about: 'About',
    cart: 'Cart',
    search: 'Search',
    login: 'Login',
    register: 'Register',
    logout: 'Logout',
    account: 'My Account',
    addToCart: 'Add to Cart',
    buyNow: 'Buy Now',
    viewDetails: 'View Details',
    price: 'Price',
    quantity: 'Quantity',
    total: 'Total',
    checkout: 'Checkout',
    continueShopping: 'Continue Shopping',
    emptyCart: 'Your cart is empty',
    featured: 'Featured Products',
    newArrivals: 'New Arrivals',
    onSale: 'On Sale',
    categories: 'Categories',
    filters: 'Filters',
    sortBy: 'Sort by',
    currency: 'Currency',
    language: 'Language',
    shipping: 'Shipping',
    payment: 'Payment',
    orderSummary: 'Order Summary',
    placeOrder: 'Place Order',
    payOnDelivery: 'Pay on Delivery',
    payWithCard: 'Pay with Card',
    subtotal: 'Subtotal',
    tax: 'Tax',
    shippingAddress: 'Shipping Address',
    billingAddress: 'Billing Address',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    city: 'City',
    country: 'Country',
    postalCode: 'Postal Code',
    guestCheckout: 'Guest Checkout',
    readMore: 'Read More',
    allRightsReserved: 'All rights reserved',
    privacyPolicy: 'Privacy Policy',
    termsConditions: 'Terms & Conditions',
    heroTitle: 'Luxury and Comfort for Your Home',
    heroSubtitle: 'Discover our premium bedding collection',
    shopNow: 'Shop Now',
    discoverCollection: 'Discover Collection',
    qualityMaterials: 'Quality Materials',
    freeShipping: 'Free Shipping',
    freeShippingDesc: 'For orders over 349 RON',
    easyReturns: '30 Days Returns',
    easyReturnsDesc: 'Return policy',
    securePayment: 'Secure Payment',
    securePaymentDesc: 'SSL encrypted checkout',
    // Footer pages
    information: 'Information',
    paymentMethods: 'Payment Methods',
    shippingDelivery: 'Shipping and Delivery',
    returnPolicy: 'Return Policy',
    returnForm: 'Return Form',
    ranistore: 'RaniStore',
    aboutUs: 'About Us',
    cookiesPolicy: 'Cookies Policy',
    personalData: 'Processing of personal data',
    anpc: 'ANPC',
  }
};

export const AppProvider = ({ children }) => {
  const [language, setLanguage] = useState('ro');
  const [currency, setCurrency] = useState('ron');
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);

  const t = useCallback((key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  }, [language]);

  const getLocalizedText = useCallback((textObj) => {
    if (!textObj) return '';
    // Prioritize current language, then Romanian, then English
    return textObj[language] || textObj['ro'] || textObj['en'] || '';
  }, [language]);

  const formatPrice = useCallback((prices) => {
    if (!prices) return '';
    const price = prices[currency] || prices['ron'] || 0;
    const symbol = currency === 'eur' ? '€' : 'RON';
    return `${price.toFixed(2)} ${symbol}`;
  }, [currency]);

  const getPrice = useCallback((prices) => {
    if (!prices) return 0;
    return prices[currency] || prices['ron'] || 0;
  }, [currency]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API}/settings`);
        const data = await res.json();
        setSettings(data);
      } catch (err) {
        console.error('Failed to fetch settings', err);
      }
    };

    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API}/categories`);
        const data = await res.json();
        setCategories(data);
      } catch (err) {
        console.error('Failed to fetch categories', err);
      }
    };

    fetchSettings();
    fetchCategories();

    // Load cart from localStorage
    const savedCart = localStorage.getItem('rani_cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }

    // Load language/currency preferences
    const savedLang = localStorage.getItem('rani_language');
    const savedCurrency = localStorage.getItem('rani_currency');
    if (savedLang) setLanguage(savedLang);
    if (savedCurrency) setCurrency(savedCurrency);

    // Check for customer token
    const token = localStorage.getItem('rani_customer_token');
    if (token) {
      fetch(`${API}/customers/me`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => res.json()).then(data => {
        if (data.id) setCustomer(data);
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('rani_cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem('rani_language', language);
  }, [language]);

  useEffect(() => {
    localStorage.setItem('rani_currency', currency);
  }, [currency]);

  const addToCart = (product, quantity = 1) => {
    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) {
        return prev.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { product_id: product.id, product, quantity }];
    });
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
  };

  const updateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prev =>
      prev.map(item =>
        item.product_id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const cartTotal = cart.reduce((sum, item) => {
    const price = getPrice(item.product?.prices);
    const discount = item.product?.discount_percent || 0;
    const finalPrice = price * (1 - discount / 100);
    return sum + finalPrice * item.quantity;
  }, 0);

  const cartItemsCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const login = async (email, password) => {
    const res = await fetch(`${API}/customers/login?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`, {
      method: 'POST'
    });
    if (!res.ok) throw new Error('Invalid credentials');
    const data = await res.json();
    localStorage.setItem('rani_customer_token', data.token);
    setCustomer(data.customer);
    return data;
  };

  const register = async (customerData) => {
    const res = await fetch(`${API}/customers/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(customerData)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Registration failed');
    }
    const data = await res.json();
    localStorage.setItem('rani_customer_token', data.token);
    setCustomer(data.customer);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('rani_customer_token');
    setCustomer(null);
  };

  return (
    <AppContext.Provider
      value={{
        language,
        setLanguage,
        currency,
        setCurrency,
        cart,
        addToCart,
        removeFromCart,
        updateCartQuantity,
        clearCart,
        cartTotal,
        cartItemsCount,
        customer,
        login,
        register,
        logout,
        settings,
        categories,
        t,
        getLocalizedText,
        formatPrice,
        getPrice,
        API,
        BACKEND_URL,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};
